/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: PrivacyPolicy
 * File     : PrivacyPolicy.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the PrivacyPolicy view and coordinating data fetching.
 *   Exports: PrivacyPolicy
 *   
 *
 * Dependencies:
 *   - @mui/material
 *   - react
 *
 * Usage:
 *   Use in Router: <Route path="/privacypolicy" element={<PrivacyPolicy />} />
 * -----------------------------------------------------------------------------
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>Privacy Policy</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>Last updated: 2025</Typography>

      <Typography paragraph>
        This Privacy Policy explains how ReelConsensus collects, uses, and protects information when you use the
        website and related services. By using the site you consent to this Policy.
      </Typography>

      <Typography variant="h6" gutterBottom>Information We Collect</Typography>
      <Typography paragraph>
        Account details (e.g., username, email), poll and vote activity, ratings, and optional Club content you submit.
        Physical Media Collection data including movie metadata, images, purchase information, and collection details imported from external sources or manually entered.
        Technical information such as IP address, device/browser metadata, and referral/UTM parameters may be collected on your first visit for analytics and security.
      </Typography>

      <Typography variant="h6" gutterBottom>How We Use Information</Typography>
      <Typography paragraph>
        To operate polls, authenticate users, prevent abuse, provide real‑time features, improve the experience,
        and comply with legal obligations. Physical Media Collection data is used to enhance poll creation by allowing you to search your personal collection and to provide collection management features. We record first‑touch referral information and anonymous attribution events (session ID, referrer, UTM, landing URL) to understand how the service is discovered. Stripe Checkout may receive UTM metadata to help us attribute subscriptions. ReelConsensus does not host or stream media; Plex integration is for listing titles you already own or have lawful access to so you can create and vote on polls.
        Limited data may be shared with service providers (e.g., Stripe for billing, Plex/OMDb for content enrichment, Watchmode for streaming sources, Discord for OAuth/webhooks) as necessary to deliver features.
      </Typography>

      <Typography variant="h6" gutterBottom>Cookies</Typography>
      <Typography paragraph>
        We use cookies/local storage for authentication, preferences, and performance (e.g., JWT session token, UI settings).
        You can disable cookies in your browser, but some features may not work correctly.
      </Typography>

      <Typography variant="h6" gutterBottom>Data Retention & Security</Typography>
      <Typography paragraph>
        We retain data for as long as needed to provide the service and secure it using industry‑standard measures.
      </Typography>

      <Typography variant="h6" gutterBottom>Your Choices</Typography>
      <Typography paragraph>
        You may request account deletion or update your information via the account or admin tools where permitted. Email verification
        (TOTP) is used to secure new registrations. You can manage club membership and visibility via in‑app settings. We may capture
        referral metadata (document.referrer and UTM parameters) on first visit to understand how users discover the service.
        Club Streamer chat messages are retained for 30 days and then automatically deleted for privacy. We also maintain a limit of 1000 messages per club and compress older messages to optimize database performance. Our chat system includes spam detection with rate limiting (10 messages per minute), similarity checking, and pattern detection to maintain a positive user experience. You can request immediate deletion of your chat messages through our GDPR data deletion process.
      </Typography>
      <Typography paragraph>
        GDPR/CCPA: You have the right to request deletion of your personal data. Platform Owners/Admins can perform a GDPR wipe that deletes comments, Club Streamer chat messages, anonymizes votes, reassigns your clubs and created polls to the Owner, and removes your account. Contact info@reelconsensus.com if you need help.
      </Typography>

      <Typography variant="h6" gutterBottom>Contact</Typography>
      <Typography paragraph>
        For privacy questions, contact: info@reelconsensus.com.
      </Typography>
    </Box>
  );
};

export default PrivacyPolicy;


